/**
 * ServiceTherapistSelector - Paso 1: Configuración completa de la consulta
 * Pantalla profesional que incluye: Tipo + Terapeuta + Duración + Modalidad + Resumen
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useBooking } from '../BookingContext';
import { useTerapeutas } from '../../../hooks/useTerapeutas';
import MinimalistSummary from '../MinimalistSummary';
import { PROMO_ONLINE, promoOnlineVigente } from '../../../lib/utils/promoOnline';
import { getSessionPrice } from '../../../lib/utils/pricing';
import './ServiceTherapistSelector.css';

// Configuración estática de servicios (fuera del componente para evitar re-renders)
const therapyTypes = [
  { 
    id: 'individual', 
    name: 'Terapia Individual', 
    icon: '👤', 
    description: 'Sesiones personalizadas enfocadas en tu bienestar' 
  },
  { 
    id: 'pareja', 
    name: 'Terapia de Parejas', 
    icon: '💑', 
    description: 'Fortalece la comunicación y vínculo en tu relación' 
  }
];

// Solo duraciones: el precio sale de getSessionPrice (lib/utils/pricing.ts), que
// depende de modalidad y método de pago. Familia deshabilitada (FUNC-131).
const durationsByType = {
  individual: [
    { minutes: 45, name: 'Estándar' },
    { minutes: 90, name: 'Extendida' }
  ],
  pareja: [
    { minutes: 50, name: 'Estándar' },
    { minutes: 90, name: 'Extendida' }
  ]
};

interface ServiceTherapistSelectorProps {
  onNext: () => void;
  onPrevious: () => void;
  preSelectedTherapistId?: string;
}

const ServiceTherapistSelector: React.FC<ServiceTherapistSelectorProps> = ({ onNext, onPrevious, preSelectedTherapistId }) => {
  const { state, setService, setTherapist } = useBooking();
  // Método de pago por defecto = transferencia/efectivo (precio preferente); se elige en el último paso.
  const metodo = state.metodoPagoDeclarado;
  const { terapeutasExtended, loading: terapeutasLoading } = useTerapeutas();

  // Estados locales — se siembran desde el contexto para que "Retomar" (UX_VISION P1-5)
  // muestre las selecciones previas en vez de arrancar en blanco. Sin progreso guardado
  // el contexto viene vacío y los defaults quedan igual que antes.
  const [selectedType, setSelectedType] = useState<string>(() => state.selectedService?.therapyType ?? '');
  const [selectedTherapistId, setSelectedTherapistId] = useState<string>(() => state.selectedTherapist?.id ?? '');
  const [selectedDuration, setSelectedDuration] = useState<number>(() => state.selectedService?.duration ?? 0);
  const [selectedModality, setSelectedModality] = useState<'presencial' | 'online' | 'domicilio'>(() =>
    state.selectedService?.modality === 'online' || state.selectedService?.modality === 'domicilio'
      ? state.selectedService.modality
      : 'presencial'
  );
  
  // Estado de animación para feedback visual inmediato
  const [animatingType, setAnimatingType] = useState<string>('');

  // Refs para autoscroll — cada sección se revela debajo de la anterior (columna
  // única, ver .configuration-content) y sin esto el usuario tiene que adivinar
  // que apareció contenido nuevo al elegir tipo/terapeuta/duración/modalidad.
  const therapistSectionRef = useRef<HTMLElement>(null);
  const durationSectionRef = useRef<HTMLElement>(null);
  const modalitySectionRef = useRef<HTMLElement>(null);
  const continueSectionRef = useRef<HTMLDivElement>(null);

  // Pre-seleccionar terapeuta si viene de un perfil (/agendar?therapistId=slug)
  useEffect(() => {
    if (preSelectedTherapistId && terapeutasExtended.length > 0 && !selectedTherapistId) {
      const match = terapeutasExtended.find(t => t.id === preSelectedTherapistId);
      if (match) setSelectedTherapistId(preSelectedTherapistId);
    }
  }, [preSelectedTherapistId, terapeutasExtended]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actualizar contexto cuando cambien las selecciones
  useEffect(() => {
    if (selectedType && selectedTherapistId && selectedDuration && terapeutasExtended.length > 0) {
      const therapist = terapeutasExtended.find(t => t.id === selectedTherapistId);
      const typeConfig = therapyTypes.find(t => t.id === selectedType);
      // Domicilio: precio fijo/90min (ver pricing.ts), no depende de durationsByType (esa
      // tabla sigue valiendo solo para presencial/online). effectiveDuration blinda
      // el contexto aunque selectedDuration haya quedado desincronizado.
      const isDomicilio = selectedModality === 'domicilio';
      const effectiveDuration = isDomicilio ? 90 : selectedDuration;
      const durationConfig = (durationsByType as any)[selectedType]?.find((d: any) => d.minutes === effectiveDuration);

      if (therapist && typeConfig && durationConfig) {
        // Configurar servicio
        setService({
          id: `${selectedType}-${effectiveDuration}`,
          therapyType: selectedType as 'individual' | 'pareja',
          name: typeConfig.name,
          duration: effectiveDuration,
          price: getSessionPrice({ tipo: selectedType, duracion: effectiveDuration, modalidad: selectedModality, metodo }) ?? 0,
          modality: selectedModality
        });

        // Configurar terapeuta
        setTherapist(therapist);
      }
    }
  }, [selectedType, selectedTherapistId, selectedDuration, selectedModality, terapeutasExtended, metodo]);

  // Domicilio no tiene selector propio de duración (flujo público: duración se
  // elige ANTES que modalidad) — si el usuario elige domicilio y después vuelve
  // a tocar una card de duración distinta a 90, esto la vuelve a fijar en 90.
  useEffect(() => {
    if (selectedModality === 'domicilio' && selectedDuration !== 90) {
      setSelectedDuration(90);
    }
  }, [selectedModality, selectedDuration]);

  // Familia hoy tiene una sola duración (90min). Si el tipo elegido trae una
  // única opción, seleccionarla sola en vez de pedir "elegí" sobre un solo ítem.
  useEffect(() => {
    const opciones = (durationsByType as any)[selectedType] as { minutes: number }[] | undefined;
    if (opciones?.length === 1 && selectedDuration !== opciones[0].minutes) {
      setSelectedDuration(opciones[0].minutes);
    }
  }, [selectedType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    if (selectedType && selectedTherapistId && selectedDuration) {
      onNext();
    }
  };

  const isComplete = Boolean(selectedType && selectedTherapistId && selectedDuration);

  // Doble rAF: si scrollIntoView corre en el mismo tick/frame que la animación
  // whileTap de Framer Motion sobre la card clickeada, el browser descarta el
  // scroll smooth sin moverse un píxel (confirmado con instrumentación real).
  // Esperar a que termine ese frame de pintado antes de pedir el scroll.
  const scrollIntoViewSoon = (ref: React.RefObject<HTMLElement | null>, block: ScrollLogicalPosition) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block });
      });
    });
  };

  // Autoscroll a cada sección recién revelada — 'start' porque siempre están
  // apiladas en columna (nunca lado a lado), así el usuario ve el título de
  // la sección nueva en vez de tener que scrollear a ciegas para notarla.
  useEffect(() => {
    if (selectedType) scrollIntoViewSoon(therapistSectionRef, 'start');
  }, [selectedType]);

  useEffect(() => {
    if (selectedTherapistId) scrollIntoViewSoon(durationSectionRef, 'start');
  }, [selectedTherapistId]);

  useEffect(() => {
    if (selectedDuration) scrollIntoViewSoon(modalitySectionRef, 'start');
  }, [selectedDuration]);

  // No usar useEffect([isComplete]): isComplete ya es true apenas se elige duración
  // (modalidad arranca con default 'presencial'), así que competía por el scroll
  // contra el efecto de arriba en el mismo tick. Acá se dispara solo cuando el
  // usuario toca modalidad explícitamente — ref ya montado porque isComplete
  // era true desde antes.
  const selectModality = (modality: 'presencial' | 'online' | 'domicilio') => {
    setSelectedModality(modality);
    // Domicilio: duración fija en 90min, sin selector — se fuerza acá porque el
    // usuario puede haber elegido 50min antes de llegar a este paso.
    if (modality === 'domicilio') {
      setSelectedDuration(90);
    }
    scrollIntoViewSoon(continueSectionRef, 'end');
  };

  return (
    <div className="service-therapist-selector">
      <div className="container">
        {/* Header */}
        <div className="step-header">
          <button className="back-button" onClick={onPrevious}>
            ← Volver
          </button>
          <div className="step-info">
            <h1>Configurar consulta</h1>
            <p>Personaliza tu experiencia terapéutica</p>
          </div>
        </div>

        <div className="configuration-content">
          {/* Sección 1: Tipo de Terapia */}
          <motion.section 
            className="config-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="section-header">
              <h2>¿Qué tipo de terapia buscas?</h2>
              <p>Selecciona la modalidad que mejor se adapte a tus necesidades</p>
            </div>
            
            <div className="therapy-types-grid">
              {therapyTypes.map(type => (
                <motion.div
                  key={type.id}
                  className={`therapy-card ${selectedType === type.id ? 'selected' : ''} ${animatingType === type.id ? 'animating' : ''}`}
                  onClick={() => {
                    // Immediate visual feedback
                    setAnimatingType(type.id);
                    
                    // Reset dependent selections when type changes
                    if (selectedType !== type.id) {
                      setSelectedTherapistId('');
                      setSelectedDuration(0);
                      // Domicilio solo existe para pareja — si el usuario cambia
                      // a otro tipo con domicilio ya elegido, hay que soltarlo.
                      if (type.id !== 'pareja' && selectedModality === 'domicilio') {
                        setSelectedModality('presencial');
                      }
                    }
                    
                    // Set new type
                    setSelectedType(type.id);
                    
                    // Clear animation state after transition
                    setTimeout(() => setAnimatingType(''), 300);
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="therapy-icon">{type.icon}</div>
                  <h3>{type.name}</h3>
                  <p>{type.description}</p>
                  {selectedType === type.id && (
                    <div className="selected-indicator">✓</div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Sección 2: Terapeuta */}
          {selectedType && (
            <motion.section
              ref={therapistSectionRef}
              className="config-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="section-header">
                <h2>Elige tu especialista</h2>
                <p>Nuestros terapeutas están especializados en brindarte el mejor cuidado</p>
              </div>
              
              <div className="therapists-grid">
                {terapeutasLoading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Cargando especialistas...</p>
                  </div>
                ) : (
                  terapeutasExtended.map(therapist => (
                    <motion.div
                      key={therapist.id}
                      className={`therapist-card ${selectedTherapistId === therapist.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTherapistId(therapist.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="therapist-photo">
                        <img 
                          src={therapist.image || therapist.imageSmall || '/assets/img/placeholder-therapist.webp'} 
                          alt={`Foto de ${therapist.name}`}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = '/assets/img/placeholder-therapist.webp';
                          }}
                        />
                      </div>
                      <div className="therapist-info bw-therapist-info">
                        <h4>{therapist.name}</h4>
                        <p className="therapist-title">{therapist.title}</p>
                        <p className="therapist-specialties">
                          {therapist.specialties?.slice(0, 2).join(', ')}
                        </p>
                      </div>
                      {selectedTherapistId === therapist.id && (
                        <div className="selected-indicator">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="10" fill="#007AFF"/>
                            <path d="M8 10.5l2 2L15 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </motion.div>
                  ))
                )}
              </div>
            </motion.section>
          )}

          {/* Sección 3: Duración */}
          {selectedType && selectedTherapistId && (
            <motion.section
              ref={durationSectionRef}
              className="config-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="section-header">
                <h2>Duración de la sesión</h2>
                <p>Elegí la duración que mejor se adapte a tu consulta</p>
              </div>

              <div className="duration-grid">
                {(durationsByType as any)[selectedType]?.map((duration: any) => {
                  // Domicilio fija la duración en 90min sin selector — las demás
                  // cards quedan deshabilitadas (no hay "vuelta atrás" a 50min).
                  const locked = selectedModality === 'domicilio' && duration.minutes !== 90;
                  return (
                    <motion.div
                      key={duration.minutes}
                      className={`duration-card ${selectedDuration === duration.minutes ? 'selected' : ''}`}
                      onClick={() => { if (!locked) setSelectedDuration(duration.minutes); }}
                      style={locked ? { opacity: 0.4, pointerEvents: 'none', cursor: 'not-allowed' } : undefined}
                      whileHover={locked ? undefined : { scale: 1.02 }}
                      whileTap={locked ? undefined : { scale: 0.98 }}
                    >
                      <div className="duration-info">
                        <h4>{duration.minutes} minutos</h4>
                        <p className="duration-type">{duration.name}</p>
                        <div className="price-info">
                          <span className="discounted-price">
                            ${(getSessionPrice({ tipo: selectedType, duracion: duration.minutes, modalidad: selectedModality, metodo }) ?? 0).toLocaleString('es-CL')}
                          </span>
                        </div>
                      </div>
                      {selectedDuration === duration.minutes && (
                        <div className="selected-indicator">✓</div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </motion.section>
          )}

          {/* Sección 4: Modalidad */}
          {selectedType && selectedTherapistId && selectedDuration > 0 && (
            <motion.section
              ref={modalitySectionRef}
              className="config-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="section-header">
                <h2>Modalidad</h2>
                <p>Elige cómo prefieres tener tu sesión</p>
              </div>
              
              <div className={`modality-grid ${selectedType === 'pareja' ? 'modality-grid--three' : ''}`}>
                <motion.div
                  className={`modality-card presencial ${selectedModality === 'presencial' ? 'selected' : ''}`}
                  onClick={() => selectModality('presencial')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="modality-icon">🏢</div>
                  <h4>Presencial</h4>
                  <p>En nuestras cómodas oficinas</p>
                  
                  <div className="location-preview">
                    <div className="location-address">
                      <i className="fas fa-map-marker-alt"></i>
                      <span>Bombero Adolfo Ossa 1010, Ofi. 204</span>
                    </div>
                    <div className="location-area">
                      <i className="fas fa-building"></i>
                      <span>Santiago Centro, Metro Uchile</span>
                    </div>
                    <div className="location-features">
                      <span className="feature"><i className="fas fa-subway"></i> Metro cercano</span>
                    </div>
                  </div>
                  
                  {selectedModality === 'presencial' && (
                    <div className="selected-indicator">
                      <i className="fas fa-check-circle"></i>
                    </div>
                  )}
                </motion.div>

                <motion.div
                  className={`modality-card ${selectedModality === 'online' ? 'selected' : ''}`}
                  onClick={() => selectModality('online')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="modality-icon">💻</div>
                  <h4>Online</h4>
                  <p>Videollamada desde casa</p>
                  {/* FUNC-105: el precio final se aplica al elegir fecha (paso siguiente). */}
                  {selectedType === 'individual' && selectedDuration === 45 && promoOnlineVigente() && (
                    <p className="modality-promo">${PROMO_ONLINE.precio.toLocaleString('es-CL')} por sesión en octubre</p>
                  )}
                  {selectedModality === 'online' && (
                    <div className="selected-indicator">✓</div>
                  )}
                </motion.div>

                {selectedType === 'pareja' && (
                  <motion.div
                    className={`modality-card domicilio ${selectedModality === 'domicilio' ? 'selected' : ''}`}
                    onClick={() => selectModality('domicilio')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="modality-icon">🏠</div>
                    <h4>Domicilio</h4>
                    <p>El terapeuta viaja a tu hogar</p>
                    {selectedModality === 'domicilio' && (
                      <div className="selected-indicator">✓</div>
                    )}
                  </motion.div>
                )}
              </div>
            </motion.section>
          )}

          {/* Resumen Minimalista - Aparece cuando hay selecciones */}
          {!!(selectedType || selectedTherapistId || selectedDuration) && (
            <motion.section 
              className="config-section summary-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <MinimalistSummary
                therapyType={therapyTypes.find(t => t.id === selectedType)?.name}
                therapistName={selectedTherapistId ? terapeutasExtended.find(t => t.id === selectedTherapistId)?.name : undefined}
                duration={selectedDuration || undefined}
                modality={selectedModality}
                price={
                  selectedDuration
                    ? getSessionPrice({ tipo: selectedType, duracion: selectedDuration, modalidad: selectedModality, metodo }) ?? undefined
                    : undefined
                }
                compact={true}
              />
            </motion.section>
          )}

          {/* Botón de continuar - Aparece cuando la configuración está completa */}
          {isComplete && (
            <motion.div
              ref={continueSectionRef}
              className="continue-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <button 
                className="continue-button" 
                onClick={handleNext}
                disabled={!isComplete}
              >
                Continuar con fecha y hora →
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceTherapistSelector;